/**
 * Battle input seam (design D4): the one place where a battle takes player input.
 *
 * The original simulation read the player in several places: every tick proc.ongoing hands the keyboard state
 * (FWP.SPACE_PRESSED / FWP.KEY_PRESSED) to the worms while the player controls one, proc.choosing_worm and
 * proc.check_mousecontrol read the keyboard on their own (worm choice with Enter / Escape, girder shape and strike
 * direction), and the interface acts between ticks: the weapon sheet sets proc.call_weapon (close_wpsheet), a double
 * click on the map sets proc.call_click (double_clicked), the scanner spends one "ultra_scaner" of team 1
 * (new_interf.open_scaner) and the surrender button ends the battle (proc.cancel_fight). Opening and closing the
 * weapon sheet (Q, the game menu button) or the scanner only freezes the simulation (GAME_FREEZE: no tick runs; the
 * sheet's panel covers the game menu, so the scanner cannot unfreeze ticks while the sheet is open), the camera (mouse
 * drag, check_displace) is presentation: neither is input of the simulation. An online battle is not frozen by them
 * (BattleInputSource.freezesMenus false: its turn clock runs for every participant): the ticks go on while the sheet
 * is open, and the sheet stays the local player's interface, never input of its own - every participant's simulation
 * and the server's replay, where no sheet is open, must take exactly the same ticks:
 *  - while the sheet is open the worms get no keys (the original's rule: the sheet gated them), handed to them and
 *    recorded as the empty keys of the frame (begin), not withheld: proc.ongoing gates the keys on the sheet only
 *    where it freezes the battle (proc.menus_freeze);
 *  - a choice is checked when its tick starts, exactly as a recorded one (see liveMenus);
 *  - a tick whose input is known already (another participant's, or the owner's own recorded turn in the
 *    fast-forward of a reload or a rebuild, while the interface is live) is played as recorded: an open sheet closes
 *    and the interface's input made since the last tick is dropped (knownTick).
 * A replayed battle (ScriptedInput) takes no interface input at all: proc.open_wpsheet, proc.double_clicked,
 * proc.cancel_fight and new_interf.open_scaner are inert and render_game_menu hides the weapon sheet, scanner and
 * surrender buttons, as in the REPLAY mode.
 *
 * Quirk kept: before the first keyboard poll of a session FWP.KEY_PRESSED is '' (proc.ongoing runs before the stage's
 * key listener, which init_game registers), so the worms may get '' instead of 'none' on the first tick of the first
 * battle; it is recorded as 'none'. No worm is under control on that tick (first_move has not run yet) and the value
 * is overwritten on the next tick, so both replay the same.
 *
 * `BattleInput` (installed as proc.battle_input for every non-replay battle) is consulted once per tick, at the start
 * of proc.ongoing (tick = proc.battle_tick + 1, before the move logic, because the interface acts between ticks),
 * takes that tick's input from a source and hands the simulation exactly what it reads:
 *  - `LocalInput`: the live keyboard (the raw FWP values, so live play is unchanged) and the interface's calls;
 *  - `ScriptedInput`: a CommandLog (the backend's battle simulator, a rebuild of a battle): it performs the
 *    interface's between-tick events itself, after checking the conditions under which the interface offers them.
 * Whatever the source, the tick's effective input is recorded (BattleRecorder, one InputFrame per tick in global
 * ticks): `space`/`other` as the worms got them (the legacy recorder's gating), `weapon`/`click` for a call applied in
 * the tick, `key` for a key read by choosing_worm / check_mousecontrol that the worms did not get, `scanner` and
 * `surrender`. Replaying that log with ScriptedInput reproduces the battle exactly; so does replaying it anywhere else.
 * The legacy REPLAY mode (REPLAY_STRING, proc.read_key_press) does not use the seam.
 *
 * Browser-safe: no node imports.
 */
import type { BattleOutcome } from '../rules/ops/battle';
import { CommandLog, type CommandLogData, validateCommandLogData } from '../../session/command-log';
import { EMPTY_FRAME, type InputFrame, type KeyOther, frameKey, isKeyOther } from '../../session/input';
import { type StateHasher, defaultStateHasher } from '../../session/hash';
import { battleHash } from './battle-hash';
import { BattleRecorder } from './battle-recorder';
import { WIDE_MARGIN_X, WIDE_MARGIN_Y } from '../../shared/wide-view';

/** What a source returns for a tick. */
export interface SourceTick {
  /** Recorded input to perform (ScriptedInput). */
  frame?: InputFrame;
  /** Live keyboard (LocalInput): handed to the simulation as is. */
  space?: any;
  other?: any;
}

export interface BattleInputSource {
  readonly kind: string;
  /**
   * False for a source that replays recorded input: the interface's entry points into the battle (weapon sheet,
   * double click, scanner, surrender) are inert and their buttons hidden, as in the legacy REPLAY mode.
   */
  readonly interactive: boolean;
  /**
   * False when the weapon sheet and the scanner must not freeze the battle: an online battle, whose turn clock runs
   * for every participant, so a player's own menu cannot stop it. Absent / true: they pause the battle (solo).
   */
  readonly freezesMenus?: boolean;
  /** Input of `tick` (called once per tick, ticks increase by one). */
  next(tick: number): SourceTick;
  /**
   * The seam recorded the effective input of `tick` (every tick, in order, also the last tick of the battle): an
   * online source relays the local owner's input from here (src/game/runtime/lockstep-input.ts).
   */
  recorded?(tick: number, frame: InputFrame): void;
  /**
   * proc.new_move started a move of `team` in `tick` (the tick's input is recorded after this call); `hash` is the
   * battle state hash at the move start when checkpoints are on.
   */
  moveStarted?(tick: number, team: number, hash?: string): void;
}

/** The live keyboard and interface of the game. */
export class LocalInput implements BattleInputSource {
  readonly kind = 'local';
  readonly interactive = true;
  constructor(private readonly fwp: any) {}
  next(): SourceTick {
    return { space: this.fwp.SPACE_PRESSED, other: this.fwp.KEY_PRESSED };
  }
}

/**
 * Replays a CommandLog tick by tick (global ticks). Ticks beyond the end of the log are empty (the player does
 * nothing), like a turn whose input stopped.
 */
export class ScriptedInput implements BattleInputSource {
  readonly kind = 'scripted';
  readonly interactive = false;
  readonly log: CommandLog;
  /** Last tick the log covers. */
  readonly endTick: number;
  private readonly cmds: { tick: number; end: number; frame: InputFrame }[] = [];
  private cursor = 0;
  private lastTick = 0;
  /** A tick beyond the end of the log was requested. */
  beyondEnd = false;

  constructor(log: CommandLog | CommandLogData) {
    if (log instanceof CommandLog) this.log = log;
    else {
      const err = validateCommandLogData(log);
      if (err) throw new Error(`ScriptedInput: invalid command log: ${err}`);
      this.log = CommandLog.fromJSON(log);
    }
    for (const t of this.log.turns) for (const c of t.commands) this.cmds.push({ tick: c.tick, end: c.tick + c.repeat, frame: c.frame });
    const last = this.log.current;
    this.endTick = last.endTick ?? last.throughTick;
  }

  next(tick: number): SourceTick {
    if (tick > this.endTick) {
      this.beyondEnd = true;
      return { frame: { ...EMPTY_FRAME } };
    }
    if (tick < this.lastTick) this.cursor = 0;
    this.lastTick = tick;
    while (this.cursor < this.cmds.length && this.cmds[this.cursor].end < tick) this.cursor++;
    const c = this.cmds[this.cursor];
    return { frame: c && c.tick <= tick ? c.frame : { ...EMPTY_FRAME } };
  }
}

/** One recorded move start (proc.move_starts) with its checkpoint hash when checkpoints are on. */
export interface MoveCheckpoint {
  tick: number;
  team: number;
  hash?: string;
}

/** What a finished battle exposes (FWP.battle_result). */
export interface BattleRunResult {
  /** The battle's input in global ticks (one turn per move). */
  log: CommandLogData;
  /** end_game_fn's inputs in the shape of the rules module (applyBattleResult). */
  outcome: BattleOutcome;
  /** battleHash when end_game_fn ran (before the battle is torn down). */
  finalHash: string;
  /** Last battle tick of the battle (the log's last tick). */
  ticks: number;
  /** Move starts (and their hashes when checkpoints were on). */
  moves: MoveCheckpoint[];
  /** ScriptedInput: input the interface could not have produced (the battle is not a faithful record). */
  violations: string[];
  /** ScriptedInput: the log ended before the battle did (the rest was played without input). */
  logEnded: boolean;
}

export interface BattleInputOptions {
  /** Hash the battle at the start of every move (MoveCheckpoint.hash). */
  checkpoints?: boolean;
  hasher?: StateHasher;
}

const KILL_TYPES = ['p', 'e', 'a', 'c', 'h', 'f', 'l'] as const;

/** A key value as a frame key ('' before the first keyboard poll, anything unknown -> 'none'). */
function asKey(v: any): KeyOther {
  return isKeyOther(v) && v !== 'weapon' && v !== 'click' ? v : 'none';
}

interface OpenTick {
  tick: number;
  space: any;
  other: any;
  key: any;
  keyRead: boolean;
  scanner: number;
  weapon: { id: number; range: number } | null;
  click: { x: number; y: number } | null;
}

/** The seam object proc talks to (proc.battle_input). */
export class BattleInput {
  readonly recorder = new BattleRecorder(1, 1);
  readonly moves: MoveCheckpoint[] = [{ tick: 1, team: 1 }];
  readonly violations: string[] = [];
  /**
   * Interface input the seam dropped in a battle the menus do not freeze: a live weapon choice made while the ticks
   * went on, after the worm stopped being able to take a weapon (liveMenus), and the interface's input made for a tick
   * whose input is known already (knownTick). Diagnostics, tests; not input: never recorded.
   */
  readonly dropped: string[] = [];
  /** Keys of the current tick for the worms (obj.ongoing) and the legacy recorder. */
  space: any = false;
  other: any = 'none';
  /** Keyboard of the current tick for the camera (proc.check_displace; not recorded). */
  camera_key: any = 'none';
  result: BattleRunResult | null = null;
  /** Called by end_game_fn with the result (FWP.start_solo_battle `onEnd`). */
  on_end: ((result: BattleRunResult) => void) | null = null;
  private open: OpenTick | null = null;
  /** Ticks whose handler an exception aborted after the tick counter advanced (see abandon). */
  aborted = 0;
  private lastTick = 0;
  private scannerSeen: number | null = null;
  private readonly hasher: StateHasher;

  constructor(
    readonly source: BattleInputSource,
    private readonly opts: BattleInputOptions = {},
  ) {
    this.hasher = opts.hasher ?? defaultStateHasher;
  }

  get interactive(): boolean {
    return this.source.interactive;
  }

  /** The weapon sheet and the scanner freeze the battle while they are open (BattleInputSource.freezesMenus). */
  get freezesMenus(): boolean {
    return this.source.freezesMenus !== false;
  }

  /** Called once the battle is set up (after proc.init_gameplay), before its first tick. */
  attach(proc: any): void {
    this.scannerSeen = this.scannerCount(proc);
    // The checkpoint of the first move is the state the battle starts from (a different setup shows up here).
    if (this.opts.checkpoints) this.moves[0].hash = battleHash(proc.FWP, this.hasher);
    // The first move (team 1 from tick 1, proc.first_move) as a move start for the source.
    if (this.source.moveStarted) this.source.moveStarted(this.moves[0].tick, this.moves[0].team, this.moves[0].hash);
  }

  /**
   * Start of a tick (proc.ongoing after the freeze guards). Returns false when the battle ended before the tick (a
   * recorded surrender): proc.ongoing must return.
   */
  begin(proc: any): boolean {
    const tick = Number(proc.battle_tick) + 1;
    // The previous tick's handler did not finish (see abandon): the calls it applied may still be pending.
    const unfinished = this.open !== null;
    if (this.open) this.abandon(this.open, tick);
    const t: OpenTick = { tick, space: false, other: 'none', key: 'none', keyRead: false, scanner: this.scannerSpent(proc), weapon: null, click: null };
    this.open = t;
    this.lastTick = tick;
    const s = this.source.next(tick);
    let surrender = false;
    if (s.frame) {
      if (!this.freezesMenus) {
        this.knownTick(proc, tick, unfinished);
        t.scanner = this.scannerSpent(proc);
      }
      const f = s.frame;
      if (f.scanner) t.scanner += this.applyScanner(proc, f.scanner, tick);
      if (f.surrender) surrender = true;
      else if (f.other === 'weapon') this.applyWeapon(proc, f, tick);
      else if (f.other === 'click') this.applyClick(proc, f, tick);
      else {
        t.space = f.space;
        t.other = f.other;
      }
      t.key = frameKey(f);
    } else {
      t.space = s.space;
      t.other = s.other;
      t.key = s.other;
      if (!this.freezesMenus) {
        this.liveMenus(proc, tick);
        // The worms get no keys while the sheet is open (in a battle it freezes, proc.ongoing withholds them): handed
        // to them as the empty keys of the frame, which is recorded, so every simulation of this tick is the same.
        if (proc.wpsheet) {
          t.space = false;
          t.other = 'none';
          t.key = 'none';
        }
      }
    }
    // The calls pending now are applied in this tick (proc.ongoing, after the worms): they are its input.
    if (proc.call_weapon && proc.call_weapon.call) t.weapon = { id: Number(proc.call_weapon.id), range: Number(proc.call_weapon.range) };
    else if (proc.call_click && proc.call_click.call) t.click = { x: Number(proc.call_click.x), y: Number(proc.call_click.y) };
    this.space = t.space;
    this.other = t.other;
    this.camera_key = t.key;
    this.scannerSeen = this.scannerCount(proc);
    if (surrender) {
      this.recordSurrender(t);
      proc.FWP.end_game_fn(0);
      return false;
    }
    return true;
  }

  /** The keyboard as proc.choosing_worm / proc.check_mousecontrol read it (recorded when it matters). */
  read_key(): any {
    if (!this.open) return 'none';
    this.open.keyRead = true;
    return this.open.key;
  }

  /**
   * The previous tick never reached end(): an exception aborted proc.ongoing (with the browser's error policy 'log' the
   * Flash player stops the frame's handler and the game goes on - e.g. the original defect of an INITEXPLODE object
   * outside the world, which obj.explode and then obj.check_coords remove twice: Error #2025). Its input is recorded
   * now, with the keys as the tick got them (whether the worms took them is the simulation's own gating, which a
   * re-simulation computes the same way, and it aborts at the same point), so a ScriptedInput replay of the log meets
   * the same input in that tick. A tick left before proc.ongoing advanced the tick counter (proc.new_move returned
   * false, or the move logic threw) is replaced by its repetition in the next frame, which takes that frame's input.
   */
  private abandon(t: OpenTick, tick: number): void {
    this.open = null;
    if (t.tick < tick && t.tick > this.recorder.lastTick) {
      this.aborted++;
      this.record(t.tick, this.frameOf(t, true));
    }
  }

  /** End of a tick: `gated` = the worms got the keys (proc.ongoing `_loc2_`). */
  end(proc: any, gated: boolean): void {
    const t = this.open;
    if (!t) return;
    this.open = null;
    this.record(t.tick, this.frameOf(t, gated));
    // Changes inside a tick (a scanner fired as a weapon, a crate) are not the player's scanner uses.
    this.scannerSeen = this.scannerCount(proc);
  }

  /** proc.new_move started a move (true path): its first tick is the next one. */
  new_move(proc: any): void {
    const tick = Number(proc.battle_tick) + 1;
    const team = Number(proc.cur_team);
    this.recorder.newMove(tick, team);
    const m: MoveCheckpoint = { tick, team };
    if (this.opts.checkpoints) m.hash = battleHash(proc.FWP, this.hasher);
    this.moves.push(m);
    if (this.source.moveStarted) this.source.moveStarted(tick, team, m.hash);
  }

  /** The surrender button (proc.cancel_fight) between ticks: recorded before the battle ends. */
  surrender(proc: any): void {
    if (this.recorder.finished) return;
    const tick = Math.max(Number(proc.battle_tick), this.lastTick) + 1;
    this.lastTick = tick;
    this.recordSurrender({ tick, space: false, other: 'none', key: 'none', keyRead: false, scanner: this.scannerSpent(proc), weapon: null, click: null });
  }

  /** end_game_fn(winner) of a non-replay battle, before the battle is torn down: the battle's result. */
  finish(fwp: any, winner: number): BattleRunResult {
    if (this.result) return this.result;
    const p = fwp._proc;
    if (this.open) {
      // The battle ended in the move logic of a tick (proc.new_move): the between-tick part of its input counts.
      const t = this.open;
      this.open = null;
      this.record(t.tick, this.frameOf(t, false, true));
    }
    const ticks = Math.max(this.lastTick, Number(p?.battle_tick ?? 0), this.recorder.lastTick);
    this.recorder.finish(ticks);
    this.result = {
      log: this.recorder.toJSON(),
      outcome: battleOutcome(fwp, winner),
      finalHash: battleHash(fwp, this.hasher),
      ticks,
      moves: this.moves.map((m) => ({ ...m })),
      violations: [...this.violations],
      logEnded: this.source instanceof ScriptedInput ? this.source.beyondEnd : false,
    };
    return this.result;
  }

  /**
   * Online (LockstepInput): drops the interface's input made between ticks while the battle takes no local input (a
   * turn that stopped being local between two frames: another team's turn, a forced turn; the seam itself for a tick
   * whose input is known, knownTick). Between ticks only the interface sets proc.call_weapon / call_click /
   * call_open_wpsheet (the seam's own calls are applied inside the tick) and only the scanner button spends a scanner
   * of team 1, so whatever is pending now is the local player's and must not reach a tick that is not the local
   * player's: the calls are cleared and the scanners given back. Returns true when something was dropped.
   */
  discardInterfaceInput(proc: any): boolean {
    let dropped = false;
    for (const call of [proc.call_weapon, proc.call_click]) {
      if (call && call.call) {
        call.call = false;
        dropped = true;
      }
    }
    if (proc.call_open_wpsheet) {
      proc.call_open_wpsheet = false;
      dropped = true;
    }
    const spent = this.scannerSpent(proc);
    if (spent > 0) {
      proc.teams[1].weapons['ultra_scaner'][1] += spent;
      dropped = true;
      if (proc.FWP._interf && typeof proc.FWP._interf.render_game_menu === 'function') proc.FWP._interf.render_game_menu();
    }
    return dropped;
  }

  /** The recorded input so far (an open log while the battle runs). */
  get log(): CommandLogData {
    return this.recorder.toJSON();
  }

  private frameOf(t: OpenTick, gated: boolean, callsOnly = false): InputFrame {
    const f: InputFrame = { space: false, other: 'none' };
    if (t.weapon) {
      f.other = 'weapon';
      f.weapon = t.weapon.id;
      f.range = t.weapon.range;
    } else if (t.click) {
      f.other = 'click';
      f.clickX = t.click.x;
      f.clickY = t.click.y;
    } else if (gated && !callsOnly) {
      f.space = !!t.space;
      f.other = asKey(t.other);
    }
    if (t.keyRead && !callsOnly) {
      const k = asKey(t.key);
      if (k !== 'none' && k !== frameKey(f)) f.key = k as InputFrame['key'];
    }
    if (t.scanner > 0) f.scanner = t.scanner;
    return f;
  }

  private recordSurrender(t: OpenTick): void {
    this.open = null;
    const f: InputFrame = { space: false, other: 'none', surrender: true };
    if (t.scanner > 0) f.scanner = t.scanner;
    this.record(t.tick, f);
  }

  /** Records the effective input of `tick` and tells the source (BattleInputSource.recorded). */
  private record(tick: number, frame: InputFrame): void {
    this.recorder.record(tick, frame);
    if (this.source.recorded) this.source.recorded(tick, frame);
  }

  /** ultra_scaner count of team 1 (the only team the scanner spends from, new_interf.open_scaner), or null. */
  private scannerCount(proc: any): number | null {
    const w = proc.teams && proc.teams[1] && proc.teams[1].weapons ? proc.teams[1].weapons['ultra_scaner'] : undefined;
    return w ? Number(w[1]) : null;
  }

  /** Scanner uses by the interface since the end of the last tick (new_interf.open_scaner decrements the count). */
  private scannerSpent(proc: any): number {
    const now = this.scannerCount(proc);
    const seen = this.scannerSeen;
    return now !== null && seen !== null && now < seen ? seen - now : 0;
  }

  /** new_interf.open_scaner for a recorded scanner use, when the interface would have offered it. */
  private applyScanner(proc: any, n: number, tick: number): number {
    let done = 0;
    for (let i = 0; i < n; i++) {
      const w = proc.teams && proc.teams[1] ? proc.teams[1].weapons['ultra_scaner'] : undefined;
      const worm = proc.cur_worm != 0 ? proc.worms[proc.cur_worm] : null;
      if (!worm || !proc.game_control || worm.state != 'ok' || !w || w[1] == 0 || w[1] == 100) {
        this.violations.push(`tick ${tick}: scanner not available`);
        break;
      }
      --w[1];
      done++;
    }
    if (done > 0 && proc.FWP._interf && typeof proc.FWP._interf.render_game_menu === 'function') proc.FWP._interf.render_game_menu();
    return done;
  }

  /**
   * The live input of a tick of a battle the menus do not freeze (freezesMenus false: an online battle), before the
   * tick's calls are recorded. The ticks went on while the weapon sheet was open, so the worm may no longer be able to
   * take a weapon (it jumped, fell, was hit, fired, the move ended): the sheet closes without a choice, and a choice
   * the player made since the last tick counts only if the sheet could be opened now - the check a recorded choice
   * gets (applyWeapon), so every simulation that replays this tick takes the same input. A refused choice is dropped,
   * not recorded.
   */
  private liveMenus(proc: any, tick: number): void {
    if (proc.wpsheet && this.sheetRefusal(proc, false)) proc.close_wpsheet(0);
    const call = proc.call_weapon;
    if (call && call.call) {
      const reason = this.weaponRefusal(proc, Number(call.id), Number(call.range));
      if (reason) {
        call.call = false;
        this.dropped.push(`tick ${tick}: weapon: ${reason}`);
      }
    }
  }

  /**
   * A tick whose input is known (a recorded frame) in a battle the menus do not freeze: another participant's tick, or
   * the local owner's own recorded tick fast-forwarded after a reload or a rebuild while the interface is live. The
   * frame is the tick's whole input, as in every other simulation of it: an open weapon sheet closes without a choice
   * (a recorded weapon choice was made with the sheet closed, applyWeapon) and the interface's input made since the
   * last tick - a weapon choice, a map click, the arsenal button, a scanner use - is dropped (discardInterfaceInput).
   * After a tick whose handler did not finish (`unfinished`) a pending call may be that tick's own (applied again in
   * the next one, as a replay does): the interface's input is left alone then.
   */
  private knownTick(proc: any, tick: number, unfinished: boolean): void {
    if (proc.wpsheet) proc.close_wpsheet(0);
    if (!unfinished && this.discardInterfaceInput(proc)) this.dropped.push(`tick ${tick}: the interface's input of a tick whose input is known`);
  }

  /**
   * Why the weapon sheet could not be open now ('' = it could). The sheet opens (proc.open_wpsheet: the game menu
   * button, or Q at the end of a tick) only for the worm under the player's control while it may fire, stands (not
   * flying), has fired no shot of its weapon yet and is in no special state (weapon draw, landing, ...), and never in
   * map target mode. `openNow`: an open sheet counts as a refusal (a recorded choice is made with the sheet closed).
   */
  private sheetRefusal(proc: any, openNow: boolean): string {
    const worm = proc.cur_worm != 0 ? proc.worms[proc.cur_worm] : null;
    const o = worm ? proc.FWP._obj[worm.fid] : null;
    if (!worm || !o || worm.state != 'ok' || !proc.game_control || o.ai) return 'no worm under player control';
    if (proc.mousecontrol) return 'map target mode';
    if ((openNow && proc.wpsheet) || !(o.states.firecontrol && o.states.flying == 0 && o.weapon.shots_total == 0 && o.states.special == '')) {
      return 'the weapon sheet cannot be opened now';
    }
    return '';
  }

  /**
   * Why close_wpsheet(id, range) could not have happened at the start of this tick ('' = it could): the sheet could
   * be open and it offers the weapon (in the arsenal of the team, no longer under its `delay`).
   */
  private weaponRefusal(proc: any, id: number, range: number): string {
    const fwp = proc.FWP;
    let name: string | null = null;
    for (const k of Object.keys(fwp.WEAPONS_DEF)) if (fwp.WEAPONS_DEF[k].id == id) name = k;
    let reason = this.sheetRefusal(proc, true);
    if (reason) return reason;
    if (name === null) reason = `unknown weapon ${id}`;
    else if (!Number.isInteger(range) || range < 1 || range > 10) reason = `invalid rank ${range}`;
    else {
      const team = proc.teams[proc.cur_team];
      const counts = team && team.weapons ? team.weapons[name] : undefined;
      let fast = 0;
      if (fwp.player && fwp.player.perks && fwp.player.perks['fast_temp']) fast = fwp.get_perk_effect('fast_temp', fwp.player.perks.fast_temp, '');
      if (!counts || !(counts[range] > 0)) reason = `weapon ${name} rank ${range} not in the arsenal`;
      else if (fwp.WEAPONS_DEF[name].delay - fast - team.turns > 0) reason = `weapon ${name} not available yet`;
    }
    return reason;
  }

  /**
   * close_wpsheet(id, range) for a recorded weapon choice, when the weapon sheet could have been open and would have
   * offered the weapon (weaponRefusal). In a battle the sheet freezes, no tick runs while it is open (GAME_FREEZE), so
   * the state it was opened in is the state at the start of the tick the choice is applied in; in a battle it does not
   * freeze the live choice gets the same check when its tick starts (liveMenus).
   */
  private applyWeapon(proc: any, f: InputFrame, tick: number): void {
    const id = Number(f.weapon);
    const range = Number(f.range);
    const reason = this.weaponRefusal(proc, id, range);
    if (reason) {
      this.violations.push(`tick ${tick}: weapon: ${reason}`);
      return;
    }
    proc.call_weapon.call = true;
    proc.call_weapon.id = id;
    proc.call_weapon.range = range;
  }

  /** double_clicked for a recorded map click, when the map was in target mode and the camera could show the point. */
  private applyClick(proc: any, f: InputFrame, tick: number): void {
    if (!proc.mousecontrol) {
      this.violations.push(`tick ${tick}: click: no map target mode`);
      return;
    }
    const x = Number(f.clickX);
    const y = Number(f.clickY);
    const a = clickArea(proc.FWP);
    if (!(x >= a.minX && x <= a.maxX && y >= a.minY && y <= a.maxY)) {
      this.violations.push(`tick ${tick}: click: ${x},${y} is outside the map area the camera can show`);
      return;
    }
    proc.call_click.call = true;
    proc.call_click.x = x;
    proc.call_click.y = y;
  }
}

/**
 * World coordinates a double click on the map can have (proc.double_clicked: the mouse minus the camera position).
 * The click target (the game screen's inv_button) covers the window, 0..WINDOW_WIDTH x 0..WINDOW_HEIGHT; the camera
 * (proc.position_x / position_y) starts at 0, 0 and proc.game_displace keeps it within
 * WINDOW_WIDTH - LIMIT_RIGHT .. -LIMIT_LEFT and -100 .. -LIMIT_UP (the lower bound wins where they cross).
 * PORT (wide screens): a wide screen shows (and its click target covers) up to WIDE_MARGIN_X / WIDE_MARGIN_Y more
 * around the window: the area grows by that margin on every side, the same on every device (src/shared/wide-view.ts),
 * so a battle recorded on a wide screen is valid everywhere (session/online.ts onlineClickBounds: the same area). A touch
 * screen's camera goes past the world's side limits by at most that margin (FWP.camera_margin), so what it shows stays
 * within this area.
 */
export function clickArea(fwp: any): { minX: number; maxX: number; minY: number; maxY: number } {
  const w = fwp._world;
  const width = Number(fwp.WINDOW_WIDTH);
  const height = Number(fwp.WINDOW_HEIGHT);
  const left = Number(w.LIMIT_LEFT);
  const right = Number(w.LIMIT_RIGHT);
  const up = Number(w.LIMIT_UP);
  // Camera x in [min(0, width - right), max(0, -left, width - right)], camera y in [-100, max(0, -up)].
  return {
    minX: Math.min(0, left, right - width) - WIDE_MARGIN_X,
    maxX: Math.max(width, right) + WIDE_MARGIN_X,
    minY: Math.min(0, up) - WIDE_MARGIN_Y,
    maxY: height + 100 + WIDE_MARGIN_Y,
  };
}

/**
 * end_game_fn's inputs (FWP.as end_game_fn: winner, proc's achievement counters, the enemy graves left on the map in
 * object order) in the shape of the rules module's BattleOutcome.
 */
export function battleOutcome(fwp: any, winner: number): BattleOutcome {
  const p = fwp._proc;
  const mode = String(fwp._play_mode) as BattleOutcome['mode'];
  const id = Number(fwp._play_id);
  const out: BattleOutcome = { winner: Number(winner), mode };
  if (mode === 'assault') out.zone = id;
  else out.stage = id;
  if (!p) return out;
  const killed: any = {};
  for (const k of KILL_TYPES) killed[k] = Number(p.ach_killed?.[k] ?? 0);
  out.ach_killed = killed;
  out.ach_paralyzed = Number(p.ach_paralyzed);
  out.ach_annihilated = Number(p.ach_annihilated);
  out.ach_injure = !!p.ach_injure;
  out.ach_kill = !!p.ach_kill;
  const graves: { name: string; cpu_level: number }[] = [];
  for (let i = 1; i <= p.OBJ[0]; i++) {
    if (p.OBJ[i].free) continue;
    const o = fwp._obj[p.OBJ[i].fid];
    if (o && o.model == 'G' && o.wteam != 1) graves.push({ name: String(o.wname), cpu_level: Number(p.teams[o.wteam].cpu_level) });
  }
  out.graves = graves;
  return out;
}
